import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { FormField } from '@adornis/formfield/form-field.js';
import '@adornis/popover/x-dropdown-selection.js';
import { ExamType, type OSCECategory, OsceCategoriesByExamType, prettyOsceCategory } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

/**
 * @element ils-editor-multi-osce-type-picker
 */
@customElement('ils-editor-multi-osce-type-picker')
export class IlsEditorMultiOsceTypePicker extends FormField<OSCECategory[]> {
  @property({ attribute: 'exam-type' }) examType: ExamType = ExamType.FAMED;

  override render() {
    return html`
      <x-flex space="md">
        ${this.error ? html` <x-text tone="warning"> ${this.error} </x-text> ` : nothing} ${this._osceList()}
        ${this._oscePicker()}
      </x-flex>
    `;
  }

  private _osceList() {
    return html`
      <x-flex space="sm" w100> ${repeat(this.value.value ?? [], osceID => this._osceCard(osceID))} </x-flex>
    `;
  }

  private _oscePicker() {
    return html`
      <x-dropdown-selection
        ?disabled=${this.disabled}
        .emptyState=${'Keine OSCEs gefunden, ändern Sie ggfs. Ihren Suchbegriff.'}
        .selectables=${OsceCategoriesByExamType[this.examType]}
        placeholder="OSCE hinzufügen"
        .renderString=${(category: OSCECategory | undefined) => (category ? prettyOsceCategory(category) : '')}
        @value-picked=${(e: ValueEvent<Maybe<OSCECategory>>) => {
          if (!e.detail.value) return;
          if (!this.value.value) this.value.next([e.detail.value]);
          else {
            this.value.value.push(e.detail.value);
            this.value.next(this.value.value);
          }

          this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.value.value } }));
          e.currentTarget.value.next(null);
        }}
      >
      </x-dropdown-selection>
    `;
  }

  private _osceCard(osceType: OSCECategory) {
    return html`
      <x-basic-card padding="sm" horizontal crossaxis-center w100 space="sm" key=${osceType}>
        <x-text flex> ${prettyOsceCategory(osceType)} </x-text>
        ${this.disabled
          ? nothing
          : html`
              <x-button
                mode="icon"
                icon="delete"
                tone="error"
                @click=${() => {
                  if (!this.value.value) return;
                  const index = this.value.value.indexOf(osceType);
                  if (index < 0) return;
                  this.value.value.splice(index, 1);
                  this.value.next(this.value.value);
                  this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.value.value } }));
                }}
              ></x-button>
            `}
      </x-basic-card>
    `;
  }
}
